import { Injectable, inject } from '@angular/core';
import { fhirR4 } from '@smile-cdr/fhirts';
import { TranslateService } from '@ngx-translate/core';
import get from 'lodash/get';
import { AppContext } from '../models/AppContext';
import { CrdResponse } from '../models/CrdResponse';
import { LaunchConfigError } from '../models/LaunchConfigError';
import { MessageBoxState } from '../models/MessageBoxState';
import {
  COVERAGE_CHECK_FAILED,
  PersistentError,
} from '../models/PersistentError';
import { PA_NEEDED_CODES, PaNeededCode } from '../models/dtr-types';
import { DataService } from '../services/data.service';
import { EndpointService } from '../services/endpoint.service';
import { EnvService } from '../services/env.service';
import { CoverageInformationService } from '../services/coverage-information.service';
import { LoggerService } from '../services/logger.service';
import { ContextService } from './context.service';
import { LaunchParamsService } from '../services/launch-params.service';

export type MarshalDecision =
  | { kind: 'relaunch'; qr: fhirR4.QuestionnaireResponse; task?: fhirR4.Task }
  | { kind: 'cdex'; task: fhirR4.Task }
  | { kind: 'new' };

export type NewLaunchDecision =
  | {
    kind: 'launch';
    canonicalUrl: string | undefined;
    coverage: unknown;
    order: fhirR4.Resource;
  }
  | { kind: 'messageBox'; state: MessageBoxState }
  | { kind: 'persistentError'; error: PersistentError };

export interface PaParameters {
  canonicalUrl: string | undefined;
  paNeeded: PaNeededCode;
  order: fhirR4.Resource;
}

/**
 * Owns launch context resolution and the pre-population decision flow:
 * SMART context marshaling, PA-endpoint discovery and the CRD prior-auth
 * decision. Returns plain decisions; the component performs navigation.
 */
@Injectable({
  providedIn: 'root',
})
export class LaunchContextService {
  private readonly contextService = inject(ContextService);
  private readonly dataService = inject(DataService);
  private readonly endpointService = inject(EndpointService);
  private readonly launchParamsService = inject(LaunchParamsService);
  private readonly env = inject(EnvService);
  private readonly logger = inject(LoggerService);
  private readonly translate = inject(TranslateService);

  checkConfigError(): boolean {
    if (this.env.configError) {
      this.logError(this.env.configError);
      return true;
    }
    return false;
  }

  logError(err: unknown): void {
    this.logger.error(err);
  }

  handleLaunchError(err: unknown): boolean {
    this.logError(err);
    return this.dataService.sofLaunchDone;
  }

  async initAndCheckContext(advance: () => void): Promise<AppContext> {
    advance();
    const appContext = await this.contextService.getAppContext();
    this.launchParamsService.displayAndClearLaunchWarnings();
    await this.dataService.loadFhirUser();
    appContext.marshalContext =
      await this.contextService.marshalContext(appContext);
    if (appContext.fhirContext && !appContext.marshalContext?.success) {
      if (appContext.fhirContext.length === 0) {
        throw new Error(this.translate.instant('Error.NoFhirContext'));
      }
      throw new Error(this.translate.instant('Error.Marshaling'));
    }
    await this.resolveProxyContext(appContext);
    advance();
    return appContext;
  }

  async resolveProxyContext(appContext: AppContext) {
    let payorOrg: fhirR4.Organization;
    try {
      payorOrg = await this.contextService.getPayorOrg(appContext);
    } catch (err) {
      throw new LaunchConfigError(
        'Error.Persistent.Reason.NoOrganization',
        'Unable to resolve payer organization',
        { cause: err },
      );
    }
    const system =
      this.env.environment['payerOrgIdentifierSystem'] ||
      ContextService.smileOrgIdentifier;
    const identifier = payorOrg?.identifier?.find((id) => id.system === system);
    const payerOrgHeader = identifier
      ? `${identifier.system}|${identifier.value}`
      : '';
    const proxyBase = this.env.environment['epaProxyBaseUrl'] || '';
    this.endpointService.setContext(payerOrgHeader, proxyBase);
  }

  classifyMarshalContext(appContext: AppContext): MarshalDecision {
    const task = get(appContext, 'marshalContext.marshalingRequest.Task');
    const qr = get(
      appContext,
      'marshalContext.marshalingRequest.QuestionnaireResponse',
    );
    if (task && qr) return { kind: 'relaunch', qr, task };
    if (task) return { kind: 'cdex', task };
    if (qr) return { kind: 'relaunch', qr };
    return { kind: 'new' };
  }

  async resolveNewLaunch(appContext: AppContext): Promise<NewLaunchDecision> {
    const launchSuppliedOrder = await this.contextService.getOrderResource(appContext);
    const coverage = get(
      appContext,
      'marshalContext.marshalingRequest.Coverage',
    );

    // With no payer answer on the order, DTR mode lets the payer decide if Questionnaires need to be
    // filled out from the $questionnaire-package operation. Send only order + coverage: a canonical would
    // pre-empt that decision, and an extension-free order has no coverage-assertion-id to send as context.
    if (!CoverageInformationService.hasCoverageInformation(launchSuppliedOrder) &&
      this.env.environment.standalone === 'dtr') {
      return {
        kind: 'launch',
        canonicalUrl: undefined,
        coverage,
        order: launchSuppliedOrder,
      };
    }

    try {
      const paParameters: PaParameters = await this.getPAParameters(appContext, launchSuppliedOrder);
      if (paParameters.paNeeded === 'auth-needed') {
        return {
          kind: 'launch',
          canonicalUrl: paParameters.canonicalUrl,
          coverage,
          order: paParameters.order,
        };
      }
      if (paParameters.paNeeded === 'conditional') {
        return { kind: 'messageBox', state: MessageBoxState.PAConditional };
      }
      if (paParameters.paNeeded === 'no-auth') {
        return { kind: 'messageBox', state: MessageBoxState.PANotRequired };
      }
    } catch (err) {
      this.logger.error('PA Needed value could not be determined.Coverage check failed.', err);
    }
    /* TODO: Improve handling of additional paNeeded Values in EPAS-352
     * @see https://simpaticois.atlassian.net/browse/EPAS-352
     */
    return { kind: 'persistentError', error: COVERAGE_CHECK_FAILED };
  }

  async getPAParameters(appContext: AppContext, order: fhirR4.Resource): Promise<PaParameters> {
    const determination = await this.readPayerDetermination(appContext, order);
    const canonicalUrl = CoverageInformationService.getCanonicalUrl(
      determination.order,
      appContext.canonicalUrl,
    );
    const paNeeded = determination.paNeeded;
    if (!paNeeded) {
      throw new Error('The payer determination has no paNeeded value.');
    }
    if (!PA_NEEDED_CODES.includes(paNeeded)) {
      throw new Error('The paNeeded value is not valid or currently unsupported.');
    }
    return { canonicalUrl, paNeeded, order: determination.order };
  }

  /**
   * The payer's prior-auth determination, and the order it applies to.
   *
   * An order a previous CRD exchange already annotated answers for itself.
   * Otherwise the determination comes off the hook response, and the order it
   * names replaces ours — only that copy carries the payer's coverage-information.
   * `js-crd-client`'s `cards` path returns no `paNeeded`, so it ends at the
   * missing-value error.
   */
  async readPayerDetermination(
    appContext: AppContext,
    order: fhirR4.Resource,
  ): Promise<{ paNeeded: PaNeededCode | undefined; order: fhirR4.Resource }> {
    if (CoverageInformationService.hasCoverageInformation(order)) {
      return { paNeeded: CoverageInformationService.getPANeeded(order), order };
    }
    const crdResponse = await this.contextService.callCrd(appContext, order);
    return {
      paNeeded: crdResponse?.context?.paNeeded?.valueCode,
      order: (await this.resolveOrderFromCrd(crdResponse)) ?? order,
    };
  }

  /**
   * The CRD `systemActions` path returns the payer-annotated order inline; the
   * `cards` path returns only a reference (`orderId`, or an `order` array of
   * references), which has to be fetched before its extensions can be read.
   */
  async resolveOrderFromCrd(
    crdResponse: CrdResponse,
  ): Promise<fhirR4.Resource | undefined> {
    const order = crdResponse?.context?.order;
    if (order && !Array.isArray(order)) {
      return order;
    }
    const reference = order?.[0] ?? crdResponse?.context?.orderId;
    if (!reference) {
      return undefined;
    }
    return await this.dataService.getResource(reference);
  }
}
